import type {
    ApiSmSpinChaseResult,
    ApiSmSpinV2Res as ApiSmSpinRes,
    ApiSmStateSnapshot,
} from "@bridge-applications/pb-types";

/** Do not let a state read racing a spin restore an older wallet or Career. */
export function reconcileSlotSnapshot(
    previous: ApiSmStateSnapshot | null | undefined,
    incoming: ApiSmStateSnapshot,
): ApiSmStateSnapshot {
    if (!previous || previous.player.userId !== incoming.player.userId)
        return incoming;
    if (
        (previous.campaign.campaignId === incoming.campaign.campaignId &&
            BigInt(previous.player.stateVersion) >
                BigInt(incoming.player.stateVersion)) ||
        previous.balances.some((balance) => {
            const next = incoming.balances.find(
                ({ currencyCode }) => currencyCode === balance.currencyCode,
            );
            return (
                next !== undefined &&
                BigInt(balance.version) > BigInt(next.version)
            );
        })
    )
        return previous;
    const before = previous.careerAvailability;
    const after = incoming.careerAvailability;
    if (before && after && "careerVersion" in before) {
        let staleCareer =
            after.status === "not-initialized" ||
            ("careerVersion" in after &&
                BigInt(before.careerVersion) > BigInt(after.careerVersion));
        if (
            before.status === "ready" &&
            after.status === "ready" &&
            before.jobRunId === after.jobRunId
        ) {
            staleCareer ||=
                BigInt(before.jobVersion) > BigInt(after.jobVersion) ||
                (before.pricingVersion !== null &&
                    (after.pricingVersion === null ||
                        BigInt(before.pricingVersion) >
                            BigInt(after.pricingVersion)));
        }
        // A navigation hint must never discard a newer settled spin or wallet.
        if (staleCareer)
            return {
                ...incoming,
                careerAvailability: { status: "unavailable" },
            };
    }
    return incoming;
}

function previousChaseIsStillActive(
    previous: ApiSmStateSnapshot,
    incoming: ApiSmStateSnapshot,
) {
    // Be defensive at this client boundary: a page that was already open
    // while pb-types was upgraded can still hold a v1.2 snapshot where the
    // chase field is absent rather than explicitly null.
    const previousChase = previous.chase;
    if (!previousChase) return false;

    return (
        previous.campaign.campaignId === incoming.campaign.campaignId &&
        new Date(previousChase.endsAt).getTime() >
            new Date(incoming.observedAt).getTime()
    );
}

/**
 * The spin endpoint deliberately returns a compact chase delta instead of
 * re-reading the full chase snapshot after settlement. Merge that delta into
 * the auth/state snapshot already held by the client.
 */
export function mergeSpinSnapshot(
    previous: ApiSmStateSnapshot | undefined,
    nextSnapshot: ApiSmStateSnapshot,
    delta: ApiSmSpinChaseResult | null,
    snapshotIsComplete = false,
): ApiSmStateSnapshot {
    const incoming = reconcileSlotSnapshot(previous, nextSnapshot);
    if (incoming === previous) return incoming;
    if (
        snapshotIsComplete ||
        incoming.chase ||
        !previousChaseIsStillActive(previous ?? incoming, incoming)
    ) {
        return incoming;
    }

    const previousChase = previous?.chase;
    if (!previousChase) return incoming;
    if (delta && delta.chaseId !== previousChase.chaseId) return incoming;

    if (!delta) {
        return { ...incoming, chase: previousChase };
    }

    const newGrants = new Map(
        delta.milestoneGrants.map((grant) => [grant.milestoneId, grant]),
    );
    return {
        ...incoming,
        chase: {
            ...previousChase,
            points: delta.points,
            scoredSpinsCount: delta.scoredSpinsCount,
            progressVersion: delta.progressVersion,
            milestones: previousChase.milestones.map((milestone) => {
                const grant = newGrants.get(milestone.milestoneId);
                if (!grant) return milestone;
                return {
                    ...milestone,
                    state: "EARNED" as const,
                    grant: {
                        grantId: grant.grantId,
                        awardedAt: grant.awardedAt,
                    },
                };
            }),
        },
    };
}

/** Response metadata stays local; the published API JSON is unchanged. */
export type ClientSpinResponse = Extract<ApiSmSpinRes, { status: "OK" }> & {
    snapshotIsComplete: boolean;
};

export function clientSpinResponse(
    response: Extract<ApiSmSpinRes, { status: "OK" }>,
    transportRetried: boolean,
): ClientSpinResponse {
    return {
        ...response,
        // Only Worker recovery responses set replayed=true and contain full
        // current state. A local transport retry may still return compact state.
        snapshotIsComplete: response.replayed,
        replayed: response.replayed || transportRetried,
    };
}

type SuccessfulSpinSnapshot = Pick<
    ClientSpinResponse,
    "replayed" | "configVersion" | "snapshot" | "chase" | "snapshotIsComplete"
>;

/** Explicit recovery metadata distinguishes full state from compact deltas. */
export function mergeSpinResponseSnapshot(
    previous: ApiSmStateSnapshot | undefined,
    response: SuccessfulSpinSnapshot,
): ApiSmStateSnapshot {
    return mergeSpinSnapshot(
        previous,
        response.snapshot,
        response.chase,
        response.snapshotIsComplete,
    );
}
